import { useEffect, useRef, useState } from 'react';
import { fmtMMSS } from '@busy/core';
import { ModalCloseButton, ModalShell } from '@busy/ui';
import { useT } from '../../lib/i18n';
import type { ServicePhase } from '@busy/api';

// Live preview / "test run" the cut without saving a visit. Lets the
// barber feel the rhythm of their phase design before relying on it on
// a real client. Ticks at 4× speed by default — a 30-min cut runs in
// ~7.5 min — so the barber gets a genuine sense of pacing without
// burning their afternoon.
//
// Standalone from LiveTimer + the cut-timer store on purpose: this
// doesn't touch persisted state, won't fire achievement triggers,
// can't accidentally log a visit. It's strictly visual.

const SPEED = 4;
const TICK_MS = 100;

export function CutPreviewModal({
  cutName,
  phases,
  onClose,
}: {
  cutName: string;
  phases: ServicePhase[];
  onClose: () => void;
}) {
  const t = useT();
  const totalSec = phases.reduce((s, p) => s + p.sec, 0);
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [phaseElapsed, setPhaseElapsed] = useState(0);
  const [paused, setPaused] = useState(false);
  const [done, setDone] = useState(false);
  const lastTickRef = useRef<number>(performance.now());

  useEffect(() => {
    if (paused || done || phases.length === 0) return;
    const id = window.setInterval(() => {
      const now = performance.now();
      const dt = (now - lastTickRef.current) / 1000;
      lastTickRef.current = now;
      setPhaseElapsed((prev) => {
        const next = prev + dt * SPEED;
        const cur = phases[phaseIdx];
        if (!cur) return prev;
        if (next >= cur.sec) {
          // Advance phase. If last phase, finish.
          if (phaseIdx >= phases.length - 1) {
            setDone(true);
            return cur.sec;
          }
          setPhaseIdx((i) => i + 1);
          return 0;
        }
        return next;
      });
    }, TICK_MS);
    return () => window.clearInterval(id);
  }, [paused, done, phaseIdx, phases]);

  // Reset the lastTickRef whenever pause flips — otherwise the unpause tick
  // sees a giant `dt` and skips the cut forward by however long we paused.
  useEffect(() => {
    lastTickRef.current = performance.now();
  }, [paused]);

  const cur = phases[phaseIdx];
  const phaseTotalSec = cur?.sec ?? 0;
  const phaseProgress = phaseTotalSec > 0 ? Math.min(1, phaseElapsed / phaseTotalSec) : 0;

  const elapsedTotalSec = phases
    .slice(0, phaseIdx)
    .reduce((s, p) => s + p.sec, 0) + phaseElapsed;
  const totalProgress = totalSec > 0 ? Math.min(1, elapsedTotalSec / totalSec) : 0;

  function restart() {
    setPhaseIdx(0);
    setPhaseElapsed(0);
    setDone(false);
    setPaused(false);
    lastTickRef.current = performance.now();
  }

  function skipPhase() {
    if (done) return;
    if (phaseIdx >= phases.length - 1) {
      setDone(true);
      return;
    }
    setPhaseIdx((i) => i + 1);
    setPhaseElapsed(0);
    lastTickRef.current = performance.now();
  }

  return (
    <ModalShell open onClose={onClose} variant="dark" size="md" ariaLabel={t('cutPreview.aria')} dismissOnBackdrop={false}>
      <div className="flex flex-col">
        <ModalCloseButton onClose={onClose} variant="dark" />
        <div className="shrink-0 px-6 pt-7 pb-2 relative">
          <div className="text-[11px] tracking-[0.18em] uppercase font-extrabold text-gold-400 mb-1.5 inline-flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-gold-400 animate-pulse" />
            {t('cutPreview.eyebrow', { x: SPEED })}
          </div>
          <h2 className="font-display text-2xl text-white leading-tight">{cutName}</h2>
        </div>

        <div className="flex-1 px-6 py-6 text-white text-center min-h-0">
          {done ? (
            <div className="space-y-3 py-8">
              <div className="text-[11px] tracking-[0.18em] uppercase font-extrabold text-gold-400">
                {t('cutPreview.completeEyebrow')}
              </div>
              <h3 className="font-display text-3xl">{t('cutPreview.completeTitle')}</h3>
              <p className="text-white/70 text-sm leading-relaxed max-w-xs mx-auto">
                {t('cutPreview.completeBody')}
              </p>
            </div>
          ) : (
            <>
              <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-gold-400 mb-1.5">
                {t('cutPreview.phase', { current: phaseIdx + 1, total: phases.length })}
              </div>
              <div className="font-display text-3xl mb-1">{cur?.name || t('cutPreview.noName')}</div>
              <div className="font-display text-[64px] leading-none tabular-nums text-gold-400 mb-2">
                {fmtMMSS(Math.max(0, Math.round(phaseTotalSec - phaseElapsed)))}
              </div>
              <div className="text-[11px] tracking-[0.14em] uppercase font-bold text-white/50 mb-5">
                {t('cutPreview.phaseRemaining')}
              </div>

              {/* Phase progress bar — fills as the current phase elapses. */}
              <div className="h-1.5 rounded-full bg-white/10 overflow-hidden mb-6">
                <div
                  className="h-full bg-gold-500 transition-[width] duration-150"
                  style={{ width: `${Math.round(phaseProgress * 100)}%` }}
                />
              </div>

              {/* Phase markers — small ticks under the progress so the barber
                  sees where they are in the overall flow. */}
              <div className="flex gap-1 mb-2">
                {phases.map((p, i) => {
                  const cumulative = phases.slice(0, i + 1).reduce((s, pp) => s + pp.sec, 0);
                  const fill = totalSec > 0 ? Math.max(0, Math.min(1, (elapsedTotalSec) / cumulative)) : 0;
                  const isCurrent = i === phaseIdx;
                  return (
                    <div
                      key={i}
                      className={`flex-1 h-1 rounded-full bg-white/15 overflow-hidden ${
                        isCurrent ? 'ring-1 ring-gold-400/60' : ''
                      }`}
                      title={`${p.name || ''} · ${fmtMMSS(p.sec)}`}
                    >
                      <div
                        className="h-full bg-gold-500"
                        style={{ width: `${Math.round(fill * 100)}%` }}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="text-[11px] text-white/55 tabular-nums">
                {t('cutPreview.totalProgress', {
                  done: fmtMMSS(Math.round(elapsedTotalSec)),
                  total: fmtMMSS(totalSec),
                  pct: Math.round(totalProgress * 100),
                })}
              </div>
            </>
          )}
        </div>

        <div className="shrink-0 border-t border-white/10 bg-navy-900 p-4 flex gap-2">
          {done ? (
            <>
              <button
                type="button"
                onClick={restart}
                className="flex-1 h-11 rounded-lg bg-gold-500 text-navy-900 font-bold inline-flex items-center justify-center gap-2"
              >
                <RestartIcon className="w-4 h-4" />
                {t('cutPreview.restart')}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 h-11 rounded-lg border-2 border-white/20 text-white font-semibold"
              >
                {t('cutPreview.close')}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setPaused((p) => !p)}
                className="h-11 px-4 rounded-lg border-2 border-white/20 text-white font-bold inline-flex items-center justify-center gap-1.5"
              >
                {paused ? <PlayIcon className="w-3.5 h-3.5" /> : <PauseIcon className="w-3.5 h-3.5" />}
                {paused ? t('cutPreview.resume') : t('cutPreview.pause')}
              </button>
              <button
                type="button"
                onClick={skipPhase}
                className="h-11 px-4 rounded-lg border-2 border-white/20 text-white font-bold inline-flex items-center justify-center gap-1.5"
              >
                {t('cutPreview.skip')}
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 h-11 rounded-lg bg-gold-500 text-navy-900 font-bold"
              >
                {t('cutPreview.stop')}
              </button>
            </>
          )}
        </div>
      </div>
    </ModalShell>
  );
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <polygon points="6 4 20 12 6 20 6 4" />
    </svg>
  );
}

function PauseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <rect x="6" y="4" width="4" height="16" />
      <rect x="14" y="4" width="4" height="16" />
    </svg>
  );
}

function RestartIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="23 4 23 10 17 10" />
      <path d="M20.49 15A9 9 0 1 1 5.64 5.64L23 10" />
    </svg>
  );
}

function ChevronRight({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}
